import {
  ArrowRightIcon,
  ChevronLeftIcon,
  PlusIcon,
  StarIcon,
  XMarkIcon
} from '@heroicons/react/20/solid';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import { captureException } from 'src/services/reporting';
import { Rfq } from 'src/shared/types/rfq';
import { UploadedFile } from 'src/shared/types/uploadedFile';

import {
  cn,
  Drawer,
  DrawerContent,
  Image,
  Progress,
  Spinner
} from 'src/shared/components/ui';
import { ButtonV2, Input, Typography } from 'src/shared/components/ui';

import { updateRfqFileCaption } from '../../api/updateRfqFileCaption';
import { uploadRfqFileFromUrl } from '../../api/uploadRfqFileFromUrl';
import { useRfqConversationContext } from '../../context/RfqConversationContext';
import { useCaptionModal } from '../../hooks/useCaptionModal';
import { useFileCaptionManager } from '../../hooks/useFileCaptionManager';
import { useFilePreview } from '../../hooks/useFilePreview';
import useImageSearch from '../../hooks/useImageSearch';
import { useOptimisticFileUploadWithCaption } from '../../hooks/useOptimisticFileUploadWithCaption';
import BlobV2 from '../BlobV2';
import { CaptionModal } from './CaptionModal';
import { UploadFailedNotice } from './UploadFailedNotice';

interface RfqImageSelectionMobileProps {
  isOpen: boolean;
  onClose: () => void;
  rfq?: Rfq;
  /** Mode of operation: 'standalone' sends messages directly, 'builder' uses callback */
  mode?: 'standalone' | 'builder';
  /** Callback when images are selected in 'builder' mode */
  onImagesSelected?: (files: UploadedFile[]) => void;
  /** Callback when standalone send succeeds, to navigate to the next step */
  onSuccess?: () => void;
}

interface ImageSearchResult {
  url: string;
  [key: string]: any;
}

interface SelectedImage {
  url: string;
  id: string;
  file?: File;
  pendingFileId: string;
  uploadedFileId?: string;
  uploadedFile?: UploadedFile; // Full uploaded file object for builder mode
  caption: string;
  originalIndex: number;
  isUploading?: boolean;
  uploadError?: string;
  preview?: string;
  source: 'user' | 'reference'; // Track file origin: 'user' for uploads, 'reference' for reference images
}

const RfqImageSelectionMobile = ({
  isOpen,
  onClose,
  rfq,
  mode = 'standalone',
  onImagesSelected,
  onSuccess
}: RfqImageSelectionMobileProps) => {
  const [currentSearchQuery, setCurrentSearchQuery] = useState<
    string | undefined
  >(undefined);
  const [images, setImages] = useState<ImageSearchResult[]>([]);
  const [showMoreInput, setShowMoreInput] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [rateLimitError, setRateLimitError] = useState<string | null>(null);
  const [failedImages, setFailedImages] = useState<Set<number>>(new Set());
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());
  const [selectedImages, setSelectedImages] = useState<SelectedImage[]>([]);
  const [pendingImage, setPendingImage] = useState<SelectedImage | null>(null);
  const [pendingFileUploads, setPendingFileUploads] = useState<SelectedImage[]>(
    []
  );
  // Mobile-specific: drawer state for viewing images
  const [isImageDrawerOpen, setIsImageDrawerOpen] = useState(false);

  const lastRequestTimeRef = useRef<number>(0);
  const MIN_REQUEST_INTERVAL = 2000;
  const MIN_IMAGE_WIDTH = 100;
  const MIN_IMAGE_HEIGHT = 100;
  const selectedImagesRef = useRef<SelectedImage[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imagesContainerRef = useRef<HTMLDivElement>(null);
  const captionListRef = useRef<HTMLDivElement>(null);

  // Internal search term suffix (not shown to user)
  const isApparel = rfq?.rfq?.document?.category === 'apparel';
  const SEARCH_TERM_SUFFIX = isApparel
    ? 'site:zalando.co.uk'
    : 'product photography aesthetic';
  const llmSearchTerm = rfq?.rfq?.document?.image_search_term?.trim();

  // Strip any site:domain.tld prefix so it is never shown in the search input.
  const stripSitePrefix = (term: string) =>
    term.replace(/^site:\S+\s*/i, '').trim();

  // Reusable hooks
  const { setPreview, getPreview, removePreview, previewUrlsRef } =
    useFilePreview();
  const uploadHook = useOptimisticFileUploadWithCaption({
    rfqId: rfq?.rfq?.id
  });
  const {
    pendingFiles,
    addFiles: addFilesToUpload,
    removeFile: removeFileFromUpload,
    waitForUploads,
    isUploading: isUploadingFiles,
    uploadFileWithCaption,
    retryUpload
  } = uploadHook;
  const { getPendingFileByIdOrFile, updateCaptionForFile } =
    useFileCaptionManager(rfq?.rfq?.id, uploadHook);
  const { connectToConversation, isSending, setIsSending } =
    useRfqConversationContext();

  // Combine selectedImages with pendingImage and pendingFileUploads for modal display
  const modalItems = useMemo(() => {
    if (pendingImage) {
      return [pendingImage];
    }
    if (pendingFileUploads.length > 0) {
      return pendingFileUploads;
    }

    return selectedImages;
  }, [selectedImages, pendingImage, pendingFileUploads]);

  const captionModal = useCaptionModal(modalItems);
  const {
    isCaptionModalOpen,
    onCloseCaptionModal,
    currentCaptionIndex,
    captionValue,
    setCaptionValue,
    isEditMode,
    handleEditCaption,
    handleCaptionNext: captionNext,
    handleCaptionPrevious: captionPrevious,
    handleCaptionDone: captionDone,
    openModalForNewItems
  } = captionModal;

  const imageSearch = useImageSearch(currentSearchQuery);

  // Reset all state when drawer closes
  const resetState = useCallback(() => {
    setSelectedImages([]);
    setPendingImage(null);
    setPendingFileUploads([]);
    setShowMoreInput(false);
    setSearchQuery('');
    setRateLimitError(null);
    setCaptionValue('');
    setIsImageDrawerOpen(false);
  }, [setCaptionValue]);

  // Wrapper for onClose that resets state
  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  // Keep refs in sync
  useEffect(() => {
    selectedImagesRef.current = selectedImages;
    selectedImages.forEach((img) => {
      if (img.preview && !previewUrlsRef.current.has(img.id)) {
        setPreview(img.id, img.preview);
      }
    });
  }, [selectedImages, setPreview]);

  // Sync query results with images state
  useEffect(() => {
    if (imageSearch.data) {
      setImages(imageSearch.data);
      setFailedImages(new Set());
      setLoadedImages(new Set());
      setRateLimitError(null);
    } else if (imageSearch.isError) {
      setImages([]);
      setRateLimitError(null);
    }
  }, [imageSearch.data, imageSearch.isError]);

  const fetchImages = useCallback(
    (query: string) => {
      const trimmedQuery = query.trim();

      // Empty query: fall back to llmSearchTerm if available, otherwise bail
      if (!trimmedQuery && !llmSearchTerm) return;

      const now = Date.now();
      const timeSinceLastRequest = now - lastRequestTimeRef.current;

      if (timeSinceLastRequest < MIN_REQUEST_INTERVAL) {
        const waitTime = MIN_REQUEST_INTERVAL - timeSinceLastRequest;

        setRateLimitError(
          `Please wait ${Math.ceil(waitTime / 1000)} second(s) before searching again`
        );

        return;
      }

      lastRequestTimeRef.current = now;
      setRateLimitError(null);
      // Store the user's original search query for refine feature
      setUserSearchQuery(trimmedQuery);

      if (!trimmedQuery) {
        // Empty query: use backend search term directly
        setCurrentSearchQuery(llmSearchTerm!);
      } else {
        setCurrentSearchQuery(
          isApparel ? `${trimmedQuery} ${SEARCH_TERM_SUFFIX}` : trimmedQuery
        );
      }
    },
    [llmSearchTerm, isApparel, SEARCH_TERM_SUFFIX]
  );

  // Sync selectedImages with pendingFiles
  const pendingFilesMap = useMemo(() => {
    const map = new Map();

    pendingFiles.forEach((pf) => {
      if (pf.id) map.set(pf.id, pf);
      if (pf.file) {
        const key = `${pf.file.name}-${pf.file.size}-${pf.file.lastModified}`;

        map.set(key, pf);
      }
    });

    return map;
  }, [pendingFiles]);

  useEffect(() => {
    setSelectedImages((prevImages) => {
      if (prevImages.length === 0) return prevImages;

      const existingPendingIds = new Set(
        prevImages
          .filter((img) => img.pendingFileId)
          .map((img) => img.pendingFileId!)
      );
      const newPendingFiles = pendingFiles.filter(
        (pf) => !existingPendingIds.has(pf.id)
      );

      let hasChanges = false;
      const result: SelectedImage[] = [];

      for (const img of prevImages) {
        const previewUrl = getPreview(img.id) || img.preview;

        if (previewUrl && !previewUrlsRef.current.has(img.id)) {
          setPreview(img.id, previewUrl);
        }

        let updatedImage: SelectedImage = {
          ...img,
          preview: previewUrl || img.preview
        };

        if (!updatedImage.pendingFileId && img.file) {
          const matchingPendingFile = newPendingFiles.find(
            (pf) =>
              pf.file === img.file ||
              (pf.file.name === img.file!.name &&
                pf.file.size === img.file!.size &&
                pf.file.lastModified === img.file!.lastModified)
          );

          if (matchingPendingFile) {
            hasChanges = true;
            updatedImage = {
              ...updatedImage,
              pendingFileId: matchingPendingFile.id
            };
          }
        }

        if (updatedImage.pendingFileId) {
          const pendingFile = pendingFilesMap.get(updatedImage.pendingFileId);

          if (pendingFile && pendingFile.caption !== updatedImage.caption) {
            hasChanges = true;
            updatedImage = {
              ...updatedImage,
              caption: pendingFile.caption
            };
          }
        }

        result.push(updatedImage);
      }

      return hasChanges ? result : prevImages;
    });
  }, [pendingFilesMap, pendingFiles, getPreview, setPreview, previewUrlsRef]);

  const rfqDocumentName = rfq?.rfq?.document?.name;

  useEffect(() => {
    if (!isOpen) return;

    if (llmSearchTerm) {
      // LLM term already has site modifier baked in — set directly to avoid
      // fetchImages prepending the suffix a second time.
      setCurrentSearchQuery(llmSearchTerm.substring(0, 150));
      // Store the display term (no site: prefix) so the refine input is clean.
      setUserSearchQuery(stripSitePrefix(llmSearchTerm));
      lastRequestTimeRef.current = Date.now();
    } else if (rfqDocumentName) {
      fetchImages(rfqDocumentName.substring(0, 100));
    }
  }, [isOpen, llmSearchTerm, rfqDocumentName, fetchImages]);

  const handleCloseRefineSearch = useCallback(() => {
    setShowMoreInput(false);
  }, []);

  const handleOpenRefineSearch = useCallback(() => {
    // Pre-populate with the current search query for refining
    setSearchQuery(userSearchQuery);
    setShowMoreInput(true);
  }, [userSearchQuery]);

  const handleSearch = useCallback(() => {
    fetchImages(searchQuery);
    handleCloseRefineSearch();
  }, [searchQuery, fetchImages, handleCloseRefineSearch]);

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleImageError = useCallback((index: number) => {
    setFailedImages((prev) => {
      if (prev.has(index)) return prev;

      return new Set(prev).add(index);
    });
  }, []);

  const handleImageLoad = useCallback(
    (index: number, e?: React.SyntheticEvent<HTMLImageElement>) => {
      // Some servers respond with non-image content (e.g. HTML error pages) and
      // fire onLoad even though the image is actually broken (0x0 dimensions),
      // and other times the result is a tiny thumbnail/icon that is useless as
      // a reference image. Treat both as failures so we render the
      // "Image unavailable" placeholder instead of a broken/garbage image.
      if (e) {
        const img = e.currentTarget;

        if (
          img.naturalWidth < MIN_IMAGE_WIDTH ||
          img.naturalHeight < MIN_IMAGE_HEIGHT
        ) {
          setFailedImages((prev) => {
            if (prev.has(index)) return prev;

            return new Set(prev).add(index);
          });

          return;
        }
      }
      setLoadedImages((prev) => {
        if (prev.has(index)) return prev;

        return new Set(prev).add(index);
      });
    },
    []
  );

  // Check for cached images after images change.
  // For images already in the browser cache, neither onLoad nor onError fires
  // (the events happened before React attached its listeners), so we have to
  // inspect the <img> element directly. complete + dimensions above the
  // minimum means it loaded successfully; complete + zero/too-small
  // dimensions (with a non-empty src) means it failed or is unusable, so we
  // mark it as broken.
  useEffect(() => {
    if (images.length === 0) return;

    const rafId = requestAnimationFrame(() => {
      const container = imagesContainerRef.current;

      if (!container) return;

      const imgElements = container.querySelectorAll('img');

      imgElements.forEach((img, index) => {
        if (!img.complete || !img.src) return;
        if (
          img.naturalWidth >= MIN_IMAGE_WIDTH &&
          img.naturalHeight >= MIN_IMAGE_HEIGHT
        ) {
          handleImageLoad(index);
        } else {
          handleImageError(index);
        }
      });
    });

    return () => cancelAnimationFrame(rafId);
  }, [images, handleImageLoad, handleImageError]);

  const uploadImageFromUrl = async (
    url: string,
    caption: string
  ): Promise<UploadedFile> => {
    if (!rfq?.rfq?.id) {
      throw new Error('RFQ ID is required');
    }

    const response = await uploadRfqFileFromUrl({
      rfq_id: rfq.rfq.id,
      image_url: url,
      caption: caption || undefined
    });

    return response.file;
  };

  const scrollCaptionListToBottom = useCallback(() => {
    if (captionListRef.current) {
      setTimeout(() => {
        if (captionListRef.current) {
          captionListRef.current.scrollTo({
            top: captionListRef.current.scrollHeight,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, []);

  const handleImageClick = (
    e: React.MouseEvent,
    image: ImageSearchResult,
    index: number
  ) => {
    e.preventDefault();
    e.stopPropagation();

    const imageUrl = typeof image === 'string' ? image : image.url;
    const isSelected = selectedImages.some((img) => img.url === imageUrl);

    if (isSelected) {
      const imageToRemove = selectedImages.find((img) => img.url === imageUrl);

      if (imageToRemove) {
        if (imageToRemove.pendingFileId) {
          removeFileFromUpload(imageToRemove.pendingFileId);
        }
        setSelectedImages((prev) =>
          prev.filter((img) => img.id !== imageToRemove.id)
        );
      }
    } else {
      const imageId = `${imageUrl}-${Date.now()}-${Math.random()}`;
      const newPendingImage: SelectedImage = {
        url: imageUrl,
        id: imageId,
        pendingFileId: '',
        caption: '',
        originalIndex: index,
        source: 'reference' // Image from reference images
      };

      setPendingImage(newPendingImage);
      handleEditCaption(0);
    }
  };

  const handleRemoveSelectedImage = useCallback(
    (imageId: string) => {
      const imageToRemove = selectedImagesRef.current.find(
        (img) => img.id === imageId
      );

      if (!imageToRemove) return;

      removePreview(imageToRemove.id);

      if (imageToRemove.pendingFileId) {
        removeFileFromUpload(imageToRemove.pendingFileId);
      }

      setSelectedImages((prev) => {
        const filtered = prev.filter((img) => img.id !== imageId);

        if (filtered.length === 0) {
          onCloseCaptionModal();
        } else if (currentCaptionIndex >= filtered.length) {
          captionModal.setCurrentCaptionIndex(filtered.length - 1);
        }

        return filtered;
      });
    },
    [
      removeFileFromUpload,
      removePreview,
      onCloseCaptionModal,
      currentCaptionIndex,
      captionModal
    ]
  );

  const convertAndSaveCaption = async (
    image: SelectedImage,
    caption: string
  ) => {
    if (image.uploadedFileId) {
      if (!rfq?.rfq?.id) {
        throw new Error('RFQ ID is required');
      }

      try {
        await updateRfqFileCaption({
          rfq_id: rfq.rfq.id,
          file_id: parseInt(image.uploadedFileId, 10),
          caption: caption || ''
        });

        setSelectedImages((prev) =>
          prev.map((img) =>
            img.id === image.id
              ? { ...img, caption, isUploading: false, uploadError: undefined }
              : img
          )
        );
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to update caption';

        // Note: handleAxiosError already reports axios failures to Sentry.
        setSelectedImages((prev) =>
          prev.map((img) =>
            img.id === image.id
              ? { ...img, isUploading: false, uploadError: errorMessage }
              : img
          )
        );
        throw error;
      }

      return;
    }

    if (!image.file) {
      try {
        const uploadedFile = await uploadImageFromUrl(image.url, caption);

        setSelectedImages((prev) =>
          prev.map((img) =>
            img.id === image.id
              ? {
                  ...img,
                  uploadedFileId: uploadedFile.id.toString(),
                  uploadedFile,
                  caption,
                  isUploading: false,
                  uploadError: undefined
                }
              : img
          )
        );
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to upload image';

        // Note: handleAxiosError already reports axios failures to Sentry.
        setSelectedImages((prev) =>
          prev.map((img) =>
            img.id === image.id
              ? { ...img, isUploading: false, uploadError: errorMessage }
              : img
          )
        );
        throw error;
      }
    } else if (image.file) {
      const pendingFile = getPendingFileByIdOrFile(
        image.pendingFileId,
        image.file
      );

      if (pendingFile) {
        await updateCaptionForFile(pendingFile, caption).catch(() => {});
      }
    }
  };

  const addPendingImage = useCallback(async () => {
    if (pendingImage) {
      const imageToUpload = {
        ...pendingImage,
        caption: captionValue,
        isUploading: true
      };

      setSelectedImages((prev) => {
        const updated = [...prev, imageToUpload];

        return updated;
      });
      setPendingImage(null);

      convertAndSaveCaption(imageToUpload, captionValue).catch(() => {});
      scrollCaptionListToBottom();

      return;
    }

    if (pendingFileUploads.length > 0) {
      const currentPendingFile = pendingFileUploads[currentCaptionIndex];

      if (!currentPendingFile || !currentPendingFile.file) return;

      const fileToAdd = currentPendingFile.file;

      addFilesToUpload([fileToAdd]);

      const imageToAdd = {
        ...currentPendingFile,
        caption: captionValue
      };

      setSelectedImages((prev) => {
        const updated = [...prev, imageToAdd];

        return updated;
      });

      setPendingFileUploads((prev) => {
        const updated = prev.filter(
          (_, index) => index !== currentCaptionIndex
        );

        return updated;
      });

      const updatePendingFileCaption = async (retries = 5) => {
        for (let i = 0; i < retries; i++) {
          const pendingFile = getPendingFileByIdOrFile(undefined, fileToAdd);

          if (pendingFile) {
            await updateCaptionForFile(pendingFile, captionValue).catch(
              () => {}
            );

            if (!captionValue.trim() && pendingFile.status === 'pending') {
              await uploadFileWithCaption(pendingFile.id, captionValue).catch(
                () => {}
              );
            }

            return;
          }
          await new Promise((resolve) => setTimeout(resolve, 50));
        }
      };

      updatePendingFileCaption();
      scrollCaptionListToBottom();
    }
  }, [
    pendingImage,
    pendingFileUploads,
    currentCaptionIndex,
    captionValue,
    scrollCaptionListToBottom,
    addFilesToUpload,
    getPendingFileByIdOrFile,
    updateCaptionForFile,
    uploadFileWithCaption
  ]);

  const handleCaptionSave = useCallback(async () => {
    if (pendingImage || pendingFileUploads.length > 0) {
      return;
    }

    const currentImage = selectedImages[currentCaptionIndex];

    if (!currentImage) return;

    if (currentImage.file) {
      const pendingFile = getPendingFileByIdOrFile(
        currentImage.pendingFileId,
        currentImage.file
      );

      if (pendingFile) {
        await updateCaptionForFile(pendingFile, captionValue).catch(() => {});
      }
    } else {
      const needsUpload = !currentImage.uploadedFileId;

      if (needsUpload) {
        setSelectedImages((prev) =>
          prev.map((img) =>
            img.id === currentImage.id
              ? { ...img, isUploading: true, uploadError: undefined }
              : img
          )
        );

        convertAndSaveCaption(currentImage, captionValue).catch(() => {});
        await new Promise((resolve) => setTimeout(resolve, 100));
      } else if (currentImage.uploadedFileId) {
        await convertAndSaveCaption(currentImage, captionValue).catch(() => {});
      }
    }
  }, [
    selectedImages,
    pendingImage,
    pendingFileUploads,
    currentCaptionIndex,
    captionValue,
    getPendingFileByIdOrFile,
    updateCaptionForFile
  ]);

  const handleCaptionNext = useCallback(async () => {
    if (pendingImage) {
      await addPendingImage();
      onCloseCaptionModal();

      return;
    }

    if (pendingFileUploads.length > 0) {
      const hasMoreFiles = pendingFileUploads.length > 1;

      await addPendingImage();

      if (hasMoreFiles) {
        setCaptionValue('');
      } else {
        setPendingFileUploads([]);
        onCloseCaptionModal();
      }

      return;
    }

    await handleCaptionSave();
    await captionNext();
  }, [
    handleCaptionSave,
    captionNext,
    pendingImage,
    pendingFileUploads,
    currentCaptionIndex,
    addPendingImage,
    onCloseCaptionModal,
    setCaptionValue
  ]);

  const handleCaptionPrevious = useCallback(async () => {
    await handleCaptionSave();
    await captionPrevious();
  }, [handleCaptionSave, captionPrevious]);

  const handleCaptionDone = useCallback(async () => {
    if (pendingImage || pendingFileUploads.length > 0) {
      await addPendingImage();
      setPendingFileUploads([]);
      onCloseCaptionModal();

      return;
    }

    await handleCaptionSave();
    captionDone();
  }, [
    handleCaptionSave,
    captionDone,
    pendingImage,
    pendingFileUploads,
    addPendingImage,
    onCloseCaptionModal
  ]);

  const handleCaptionModalClose = useCallback(async () => {
    if (pendingImage) {
      setPendingImage(null);
      setCaptionValue('');
    }
    if (pendingFileUploads.length > 0) {
      pendingFileUploads.forEach((file) => {
        if (file.id) {
          removePreview(file.id);
        }
      });
      setPendingFileUploads([]);
      setCaptionValue('');
    }
    onCloseCaptionModal();
  }, [
    pendingImage,
    pendingFileUploads,
    onCloseCaptionModal,
    setCaptionValue,
    removePreview
  ]);

  const handleSend = async () => {
    // In builder mode with no images, just close the drawer
    if (mode === 'builder' && selectedImages.length === 0) {
      handleClose();

      return;
    }

    if (!rfq?.rfq?.id || selectedImages.length === 0) return;

    const imagesToUpload = selectedImages.filter(
      (img) => !img.uploadedFileId && !img.file
    );

    if (imagesToUpload.length > 0) {
      try {
        for (const image of imagesToUpload) {
          const uploadedFile = await uploadImageFromUrl(
            image.url,
            image.caption
          );

          setSelectedImages((prev) =>
            prev.map((img) =>
              img.id === image.id
                ? {
                    ...img,
                    uploadedFileId: uploadedFile.id.toString(),
                    uploadedFile
                  }
                : img
            )
          );
        }
      } catch {
        // Note: handleAxiosError already reports axios failures to Sentry.
        // Per-image errors are surfaced in the UI via the uploadError state.
        return;
      }
    }

    if (isCaptionModalOpen && selectedImages[currentCaptionIndex]) {
      await handleCaptionSave();
    }

    setIsSending(true);

    try {
      const urlUploadedFileIds = selectedImages
        .filter((img) => img.uploadedFileId)
        .map((img) => img.uploadedFileId!);

      const uploadedFileIds = await waitForUploads();

      const hasErrors = pendingFiles.some((pf) => pf.status === 'error');

      if (hasErrors) {
        setIsSending(false);

        return;
      }

      const allFileIds = [...urlUploadedFileIds, ...uploadedFileIds];

      // In builder mode, use callback instead of sending conversation messages
      if (mode === 'builder') {
        // Collect full UploadedFile objects from both sources
        const urlUploadedFiles = selectedImages
          .filter((img) => img.uploadedFile)
          .map((img) => img.uploadedFile!);
        const fileUploadedFiles = pendingFiles
          .filter((pf) => pf.status === 'completed' && pf.uploadedFile)
          .map((pf) => pf.uploadedFile!);
        const allUploadedFiles = [...urlUploadedFiles, ...fileUploadedFiles];

        setIsSending(false);
        onImagesSelected?.(allUploadedFiles);
        handleClose();

        return;
      }

      // Standalone mode: send conversation messages
      const firstMessage = rfq?.rfq?.document?.name
        ? `Let's get started making an RFQ for a ${rfq.rfq.document.name}`
        : 'Here are my inspiration images';

      connectToConversation(firstMessage, [], false);

      setTimeout(() => {
        connectToConversation(
          `Here are my inspiration images for ${rfq.rfq.document.name}`,
          allFileIds,
          false
        );

        onSuccess?.();
      }, 3000);
    } catch (error) {
      captureException({
        error:
          error instanceof Error ? error : new Error('Failed to send images'),
        extra: {
          location: 'RfqImageSelectionMobile.handleSend',
          step: 'sendImages',
          rfqId: rfq?.rfq?.id,
          mode,
          selectedImagesCount: selectedImages.length,
          pendingFilesCount: pendingFiles.length
        }
      });
      setIsSending(false);
    }
  };

  const handleAddOwnImages = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (selectedFiles: FileList | null) => {
    if (!selectedFiles || selectedFiles.length === 0) return;

    const fileArray = Array.from(selectedFiles);
    const timestamp = Date.now();
    const newPendingFiles: SelectedImage[] = fileArray.map((file, index) => {
      const imageId = `${file.name}-${file.size}-${file.lastModified}-${timestamp}-${index}-${Math.random()}`;
      const preview = URL.createObjectURL(file);

      setPreview(imageId, preview);

      return {
        url: preview,
        id: imageId,
        file,
        pendingFileId: '',
        caption: '',
        originalIndex: index,
        preview,
        source: 'user' // File uploaded by user
      };
    });

    setPendingFileUploads(newPendingFiles);
    openModalForNewItems(0);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const imageDelays = useMemo(() => {
    if (images.length === 0) return [];
    const indices = Array.from({ length: images.length }, (_, i) => i);
    const shuffled = indices.sort(() => Math.random() - 0.5);

    return shuffled.map((_, index) => index * 0.03 + Math.random() * 0.08);
  }, [images.length]);

  const renderPreview = useCallback(
    (image: SelectedImage, previewUrl: string | undefined) => {
      if (!previewUrl) return null;

      return (
        <motion.div
          key={`${image.id}-${currentCaptionIndex}`}
          layoutId={`image-${image.url}`}
          initial={{ opacity: 0, x: 12 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -12 }}
          transition={{
            duration: 0.5,
            delay: 0.25,
            ease: [0.25, 0.1, 0.25, 1],
            layout: {
              type: 'spring',
              stiffness: 400,
              damping: 35
            }
          }}
          className="h-full"
        >
          <img
            draggable={false}
            src={previewUrl}
            alt={`Selected ${currentCaptionIndex + 1}`}
            className="h-full w-auto object-contain rounded-2xl border border-gray-4 bg-gray-2"
            onError={() => {}}
          />
        </motion.div>
      );
    },
    [currentCaptionIndex]
  );

  const getPreviewUrl = useCallback(
    (image: SelectedImage) => {
      return getPreview(image.id) || image.preview || image.url;
    },
    [getPreview]
  );

  // Mobile-specific: handle opening image drawer
  const handleViewImages = () => {
    setIsImageDrawerOpen(true);
  };

  // Mobile-specific: handle closing image drawer
  const handleCloseImageDrawer = useCallback(() => {
    setIsImageDrawerOpen(false);
    handleCloseRefineSearch();
  }, [handleCloseRefineSearch]);

  return (
    <>
      <Drawer
        isKeyboardDismissDisabled
        motionProps={{
          initial: { x: '100%' },
          animate: { x: 0 },
          exit: { x: '100%' },
          transition: {
            type: 'tween',
            duration: 0.4,
            ease: [0.4, 0, 0.2, 1]
          }
        }}
        hideCloseButton
        size="full"
        placement="right"
        isOpen={isOpen}
        onClose={handleClose}
        scrollBehavior="inside"
      >
        <DrawerContent className="relative h-dvh w-full overflow-y-auto p-0">
          {/* Header */}
          <div className="h-14 bg-white flex items-center gap-2 px-4 fixed top-0 left-0 right-0 z-[50] border-b border-gray-3">
            <div
              className="flex items-center gap-2 cursor-pointer"
              onClick={handleClose}
            >
              <ChevronLeftIcon className="size-5" />
              <Typography variant="body-1" weight="bold">
                Back
              </Typography>
            </div>
          </div>

          {/* Main Content */}
          <div className="pt-16 pb-6 px-4 flex flex-col gap-4">
            {/* Title Section */}
            <div className="flex flex-col gap-3">
              <Typography
                as="h1"
                variant="h3"
                weight="bold"
                className="text-lg"
              >
                No worries, we can build your RFQ by describing what you want to
                source
              </Typography>
              <Typography
                variant="body-1"
                className="text-sm text-secondary-text"
              >
                We&apos;ve provided images based on your description as
                references for your product
              </Typography>
            </div>

            {/* View Images Button */}
            <ButtonV2
              onPress={handleViewImages}
              color="tertiary"
              className="w-full h-12 rounded-xl"
              endContent={
                imageSearch.isFetching ? (
                  <Spinner size="sm" />
                ) : (
                  <div className="flex items-center gap-1">
                    <span className="text-xs bg-primary text-white rounded-full h-7 w-7 flex items-center justify-center">
                      {images.length}
                    </span>
                  </div>
                )
              }
            >
              View reference images
            </ButtonV2>

            {/* Selected Images List */}
            <div
              ref={captionListRef}
              className="min-h-[200px] max-h-[300px] overflow-y-auto cavela-scrollbar rounded-xl"
              style={{
                backgroundImage: `
                  linear-gradient(to right, rgba(59, 130, 246, 0.05) 1px, transparent 1px),
                  linear-gradient(to bottom, rgba(59, 130, 246, 0.05) 1px, transparent 1px)
                `,
                backgroundSize: '40px 40px',
                backgroundColor: '#FFF'
              }}
            >
              {selectedImages.length > 0 ? (
                <div className="flex flex-col gap-2 p-2">
                  <AnimatePresence>
                    {selectedImages.map((selectedImage, index) => {
                      const pendingFile = pendingFiles.find(
                        (pf) => pf.id === selectedImage.pendingFileId
                      );
                      const currentModalItem = modalItems[currentCaptionIndex];
                      const isSelected =
                        isCaptionModalOpen &&
                        currentModalItem &&
                        currentModalItem.id === selectedImage.id;

                      return (
                        <motion.div
                          key={selectedImage.id}
                          initial={{ opacity: 0, y: -8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0 }}
                          layout
                          transition={{
                            layout: {
                              duration: 0.3,
                              ease: [0.4, 0, 0.2, 1]
                            },
                            opacity: { duration: 0.2, ease: 'easeOut' },
                            y: { duration: 0.2, ease: 'easeOut' }
                          }}
                          className={cn(
                            'flex items-center gap-3 p-3 bg-gray-1 rounded-xl cursor-pointer hover:bg-gray-2 transition-colors overflow-hidden',
                            isSelected && 'bg-gray-4'
                          )}
                          onClick={() => handleEditCaption(index)}
                        >
                          <img
                            src={selectedImage.preview || selectedImage.url}
                            alt={`Selected ${index + 1}`}
                            className="w-12 h-12 object-cover rounded-lg flex-shrink-0"
                            onError={() => {}}
                          />
                          <div className="flex-1 min-w-0">
                            {selectedImage.caption ? (
                              <Typography
                                variant="body-2"
                                weight="medium"
                                className="truncate"
                              >
                                {selectedImage.caption}
                              </Typography>
                            ) : (
                              <Typography
                                variant="body-2"
                                className="text-secondary-text"
                              >
                                No caption
                              </Typography>
                            )}

                            {selectedImage.isUploading && (
                              <div className="mt-1 flex items-center gap-2">
                                <div className="size-3 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                                <Typography
                                  variant="body-2"
                                  className="text-secondary-text text-xs"
                                >
                                  Sending...
                                </Typography>
                              </div>
                            )}
                            {selectedImage.uploadError && (
                              <Typography
                                variant="body-2"
                                className="text-danger mt-1 text-xs"
                              >
                                Failed: {selectedImage.uploadError}
                              </Typography>
                            )}
                            {pendingFile?.status === 'uploading' && (
                              <div className="mt-1">
                                <Progress
                                  value={pendingFile.progress}
                                  size="sm"
                                />
                              </div>
                            )}
                            {pendingFile?.status === 'error' && (
                              <UploadFailedNotice
                                error={pendingFile.error}
                                onRetry={() => retryUpload(pendingFile.id)}
                                onRemove={() =>
                                  handleRemoveSelectedImage(selectedImage.id)
                                }
                              />
                            )}
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveSelectedImage(selectedImage.id);
                            }}
                            className="flex-shrink-0 p-1.5 hover:bg-gray-3 rounded-full transition-colors"
                            aria-label="Remove image"
                          >
                            <XMarkIcon className="size-4 text-gray-600" />
                          </button>
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full min-h-[200px]">
                  <Typography variant="body-2" className="text-secondary-text">
                    No images selected yet
                  </Typography>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-3 mt-2">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileInputChange}
                className="hidden"
              />
              <button
                onClick={handleAddOwnImages}
                className="flex items-center justify-center gap-2 text-gray-600 hover:text-dark transition-colors py-2"
              >
                <PlusIcon className="size-5" />
                <Typography variant="body-1" weight="bold">
                  Add your own images
                </Typography>
              </button>
              <ButtonV2
                onPress={handleSend}
                endContent={
                  <ArrowRightIcon className="size-4 min-h-4 min-w-4" />
                }
                isDisabled={
                  selectedImages.length === 0 ||
                  isSending ||
                  isUploadingFiles ||
                  selectedImages.some((img) => img.isUploading === true)
                }
                isLoading={isSending}
                size="lg"
                className="w-full h-14 rounded-full"
              >
                Send
              </ButtonV2>
            </div>
          </div>

          {/* Caption Modal - rendered at root level for proper z-index */}
          <CaptionModal
            isOpen={isCaptionModalOpen && !isImageDrawerOpen}
            onClose={handleCaptionModalClose}
            items={modalItems}
            currentIndex={currentCaptionIndex}
            captionValue={captionValue}
            onCaptionChange={setCaptionValue}
            isEditMode={isEditMode}
            onSave={handleCaptionSave}
            onNext={handleCaptionNext}
            onPrevious={handleCaptionPrevious}
            onDone={handleCaptionDone}
            renderPreview={renderPreview}
            getPreviewUrl={getPreviewUrl}
            isFullScreen
          />
        </DrawerContent>
      </Drawer>

      {/* Mobile Image Selection Drawer */}
      <Drawer
        isKeyboardDismissDisabled
        motionProps={{
          initial: { y: '100%' },
          animate: { y: 0 },
          exit: { y: '100%' },
          transition: {
            type: 'tween',
            duration: 0.3,
            ease: [0.4, 0, 0.2, 1]
          }
        }}
        hideCloseButton
        size="full"
        placement="bottom"
        isOpen={isImageDrawerOpen}
        onClose={handleCloseImageDrawer}
        scrollBehavior="inside"
      >
        <DrawerContent className="relative h-[90dvh] w-full rounded-t-2xl overflow-hidden">
          {/* Drawer Header */}
          <div className="h-14 bg-white flex items-center justify-between px-4 border-b border-gray-3 flex-shrink-0">
            <Typography variant="body-1" weight="bold">
              Select reference images
            </Typography>
            <button
              onClick={handleCloseImageDrawer}
              className="p-2 hover:bg-gray-2 rounded-full transition-colors"
              aria-label="Close"
            >
              <XMarkIcon className="size-5" />
            </button>
          </div>

          {/* Image Grid */}
          <div
            ref={imagesContainerRef}
            className="flex-1 overflow-y-auto p-4"
            style={{
              backgroundImage: `
                linear-gradient(to right, rgba(59, 130, 246, 0.05) 1px, transparent 1px),
                linear-gradient(to bottom, rgba(59, 130, 246, 0.05) 1px, transparent 1px)
              `,
              backgroundSize: '40px 40px',
              backgroundColor: '#FFF'
            }}
          >
            {images.length > 0 ? (
              <div className="pb-20">
                <div className="grid grid-cols-2 gap-2">
                  {images.map((image, index) => {
                    const imageUrl =
                      typeof image === 'string' ? image : image.url;
                    const hasFailed = failedImages.has(index);
                    const isSelected = selectedImages.some(
                      (img) => img.url === imageUrl
                    );
                    const isLoaded = loadedImages.has(index);
                    const isInModal =
                      isCaptionModalOpen &&
                      (modalItems[currentCaptionIndex]?.url === imageUrl ||
                        pendingImage?.url === imageUrl);

                    return (
                      <motion.div
                        key={index}
                        layoutId={
                          isSelected || isInModal
                            ? `image-mobile-${imageUrl}`
                            : undefined
                        }
                        initial={{ opacity: 0 }}
                        animate={{
                          opacity: isInModal ? 0 : 1,
                          scale: isInModal ? 0.98 : 1
                        }}
                        transition={{
                          duration: 0.5,
                          delay: imageDelays[index] || 0,
                          ease: [0.4, 0, 0.2, 1]
                        }}
                        onClick={(e) => handleImageClick(e, image, index)}
                        className={cn(
                          'border border-gray-4 relative overflow-hidden cursor-pointer hover:opacity-80 transition-all bg-gray-2 rounded-xl h-[160px]',
                          isSelected
                            ? 'shadow-[0_4px_6px_-1px_rgba(0,14,239,0.3),0_2px_4px_-1px_rgba(0,14,239,0.2)] border-primary'
                            : '',
                          isInModal && 'pointer-events-none z-50'
                        )}
                      >
                        {hasFailed ? (
                          <div className="w-full h-full bg-gray-2 border border-gray-4 rounded-xl flex flex-col items-center justify-center gap-2">
                            <Typography
                              variant="body-2"
                              className="text-gray-6 text-center px-2 text-xs"
                            >
                              Image unavailable
                            </Typography>
                          </div>
                        ) : (
                          <div className="relative w-full h-full">
                            <motion.div
                              className="absolute inset-0 w-full h-full rounded-lg"
                              initial={{
                                filter: 'blur(20px)',
                                scale: 1.1
                              }}
                              animate={{
                                filter: isLoaded ? 'blur(0px)' : 'blur(20px)',
                                scale: isLoaded ? 1 : 1.1
                              }}
                              transition={{
                                duration: 0.8,
                                ease: [0.4, 0, 0.2, 1]
                              }}
                              style={{
                                backgroundImage: `url(${imageUrl})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center'
                              }}
                            />
                            <motion.div
                              className="absolute inset-0 w-full h-full"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: isLoaded ? 1 : 0 }}
                              transition={{
                                duration: 0.6,
                                ease: [0.4, 0, 0.2, 1]
                              }}
                            >
                              <Image
                                draggable={false}
                                src={imageUrl}
                                alt={`Search result ${index + 1}`}
                                className="w-full h-full object-cover"
                                classNames={{
                                  wrapper:
                                    '!w-full !h-full !max-w-full !max-h-full',
                                  img: '!w-full !h-full object-cover max-w-full max-h-full'
                                }}
                                onLoad={(e) => handleImageLoad(index, e)}
                                onError={() => handleImageError(index)}
                              />
                            </motion.div>
                            {/* Selection indicator */}
                            {isSelected && (
                              <div className="absolute top-2 right-2 bg-primary text-white rounded-full p-1">
                                <svg
                                  className="size-4"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </div>
                            )}
                          </div>
                        )}
                      </motion.div>
                    );
                  })}
                </div>

                {/* Refine Search Button */}
                {!showMoreInput && (
                  <div className="sticky bottom-4 flex justify-center mt-4 z-10">
                    <ButtonV2
                      onPress={handleOpenRefineSearch}
                      size="md"
                      className="h-12 rounded-full px-5 text-dark border border-white bg-[#F9FAFB66]/80 backdrop-blur-md shadow-md"
                      endContent={
                        <div className="min-h-8 max-h-8 min-w-8 max-w-8 bg-white rounded-full flex items-center justify-center">
                          {imageSearch.isFetching ? (
                            <Spinner size="sm" />
                          ) : (
                            <StarIcon className="size-4" />
                          )}
                        </div>
                      }
                    >
                      Refine search
                    </ButtonV2>
                  </div>
                )}
              </div>
            ) : rateLimitError ||
              (imageSearch.isError && currentSearchQuery) ? (
              <div className="flex flex-col items-center justify-center h-full gap-2">
                <Typography
                  variant="body-1"
                  weight="medium"
                  className="text-red-600"
                >
                  {rateLimitError ||
                    imageSearch.error?.message ||
                    'Failed to fetch images'}
                </Typography>
                <Typography variant="body-2" className="text-gray-6">
                  Please try again later
                </Typography>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full">
                <BlobV2
                  speed={0.2}
                  numBlobs={6}
                  isolation={70}
                  lookAt={{ x: 0, y: 0, z: 0 }}
                  zoom={1.5}
                />
              </div>
            )}
          </div>

          {/* Search Input Overlay */}
          <AnimatePresence>
            {showMoreInput && images.length > 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: 'easeOut' }}
                className="fixed inset-0 z-[9999] flex items-center justify-center"
                onClick={(e) => {
                  if (e.target === e.currentTarget) {
                    handleCloseRefineSearch();
                  }
                }}
              >
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{
                    duration: 0.6,
                    delay: 0.1,
                    ease: [0.4, 0, 0.2, 1]
                  }}
                  className="relative bg-[#F9FAFB66]/30 backdrop-blur-md shadow-none w-full h-full p-6 flex flex-col items-center justify-center gap-6"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={handleCloseRefineSearch}
                    className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full transition-colors z-10"
                    aria-label="Close"
                  >
                    <XMarkIcon className="size-5 text-gray-700" />
                  </button>

                  <div
                    className="bg-white rounded-full px-4 py-3 shadow-lg flex items-center gap-3 w-full max-w-md"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder={`Photos of ${rfq?.rfq?.document?.name}`}
                      classNames={{
                        input: '!bg-transparent border-none focus:outline-none',
                        inputWrapper:
                          '!bg-transparent border-none shadow-none hover:!bg-transparent focus-within:!bg-transparent'
                      }}
                      className="flex-1"
                      autoFocus
                    />
                    <ButtonV2
                      onPress={handleSearch}
                      isIconOnly
                      size="sm"
                      className="!w-8 !h-8 !min-w-8 !min-h-8 rounded-full bg-primary-text"
                    >
                      <StarIcon className="size-4 text-white" />
                    </ButtonV2>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Caption Modal inside image drawer */}
          <CaptionModal
            isOpen={isCaptionModalOpen && isImageDrawerOpen}
            onClose={handleCaptionModalClose}
            items={modalItems}
            currentIndex={currentCaptionIndex}
            captionValue={captionValue}
            onCaptionChange={setCaptionValue}
            isEditMode={isEditMode}
            onSave={handleCaptionSave}
            onNext={handleCaptionNext}
            onPrevious={handleCaptionPrevious}
            onDone={handleCaptionDone}
            renderPreview={renderPreview}
            getPreviewUrl={getPreviewUrl}
            isFullScreen
          />
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default RfqImageSelectionMobile;
